import { Fragment, useMemo } from 'react';
import PropTypes from 'prop-types';
import _groupBy from 'lodash/groupBy';
import _sortBy from 'lodash/sortBy';
import _map from 'lodash/map';
import Typography from '@material-ui/core/Typography';
import Chip from '@material-ui/core/Chip';
import MailIcon from '@material-ui/icons/MailOutline';
import PhoneIcon from '@material-ui/icons/Phone';
import MLink from '@material-ui/core/Link';
import { makeStyles } from '@material-ui/core/styles';
import ApproveMemberButton from './approve-member-button';
import DenyMemberButton from './deny-member-button';
import Blockquote from '../blockquote';
import HostUser from '../host-user';

const useStyles = makeStyles((theme) => ({
  list: {
    listStyleType: 'none',
    margin: theme.spacing(2, 0),
    padding: 0
  },
  listItem: {
    margin: theme.spacing(0, 0, 1.5, 0)
  },
  memberName: {
    display: 'flex',
    alignItems: 'center'
  },
  memberEmail: {
    margin: theme.spacing(1, 0),
    display: 'flex'
  },
  memberPhone: {
    margin: theme.spacing(1, 0),
    display: 'flex'
  },
  emailLink: {
    display: 'inline-flex',
    alignItems: 'center',
    color: '#555',
    fontSize: 12,
    lineHeight: '15px'
  },
  emailIcon: {
    marginRight: theme.spacing(0.5),
    fontSize: 15
  },
  hostChip: {
    marginLeft: theme.spacing(1)
  },
  requestButtons: {
    marginTop: theme.spacing(1),
    '& > *:not(:last-child)': {
      marginRight: theme.spacing(1)
    }
  },
  requestMessage: {
    margin: theme.spacing(1, 0)
  }
}));

const AttendeesList = ({ members, event }) => {
  const classes = useStyles();

  if (!members || !members.length) {
    return <p>No attendees.</p>;
  }

  const sortedGroupedMembers = useMemo(() => {
    const sortedMembers = _sortBy(members, (member) => member.isHost ? 0 : 1);
    const membersByStatus = _groupBy(sortedMembers, 'status');
    const sorted = [
      {
        title: 'Going',
        status: 'GOING',
        statusMembers: membersByStatus['GOING'],
      },
      {
        title: 'Interested',
        status: 'INTERESTED',
        statusMembers: membersByStatus['INTERESTED'],
      }
    ];
    if (event.permissions.editable) {
      if (event.registrationType === 'APPROVAL_REQUIRED' && membersByStatus['REQUESTED']) {
        sorted.unshift({
          title: 'Requested',
          status: 'REQUESTED',
          statusMembers: membersByStatus['REQUESTED'],
        });
      }
      sorted.push({
        title: 'Not Going',
        status: 'NOT_GOING',
        statusMembers: membersByStatus['NOT_GOING'],
      });
    }
    return sorted;
  }, [event, members]);

  return (
    <Fragment>
      {_map(sortedGroupedMembers, ({ title, status, statusMembers }) => {
        const totalMemberCount = event.memberSummary[status].count;
        const memberCount = statusMembers ? statusMembers.length : 0;
        const hiddenMemberCount = totalMemberCount - memberCount;
        return (
          <Fragment key={status}>
            <Typography variant="h3">{title} ({totalMemberCount})</Typography>
            {totalMemberCount > 0 ? (
              <ul className={classes.list}>
                <Fragment>
                  {_map(statusMembers, (member) => {
                    return (
                      <li key={member.id} className={classes.listItem}>
                        <div className={classes.memberName}>
                          <HostUser user={member.user} />
                          {member.isHost && (
                            <Chip
                              size="small"
                              label="Host"
                              className={classes.hostChip}
                              color="primary"
                            />
                          )}
                        </div>
                        {member.emailVisible && member.user.email && (
                          <div className={classes.memberEmail}>
                            <MLink href={`mailto:${member.user.email}`} className={classes.emailLink}>
                              <MailIcon className={classes.emailIcon} />
                              {member.user.email}
                            </MLink>
                          </div>
                        )}
                        {member.phoneNumberVisible && member.user.phoneNumberDisplay && (
                          <div className={classes.memberPhone}>
                            <MLink href={member.user.phoneNumberUri} className={classes.emailLink}>
                              <PhoneIcon className={classes.emailIcon} />
                              {member.user.phoneNumberDisplay}
                            </MLink>
                          </div>
                        )}
                        {event.permissions.editable && member.status === 'REQUESTED' && (
                          <Fragment>
                            {member.requestMessage && (
                              <Blockquote className={classes.requestMessage}>
                                {member.requestMessage}
                              </Blockquote>
                            )}
                            <div className={classes.requestButtons}>
                              <ApproveMemberButton member={member} />
                              <DenyMemberButton member={member} />
                            </div>
                          </Fragment>
                        )}
                      </li>
                    );
                  })}
                </Fragment>
                {hiddenMemberCount > 0 && (
                  <li className={classes.hiddenItem}>{hiddenMemberCount} hidden</li>
                )}
              </ul>
            ) : (
              <p>None</p>
            )}
          </Fragment>
        );
      })}
    </Fragment>
  );
};

AttendeesList.propTypes = {
  members: PropTypes.array.isRequired,
  event: PropTypes.object.isRequired,
};

export default AttendeesList;
